import { useState, useEffect, type FormEvent } from 'react';
import { api, ApiRequestError } from '../../lib/api';

interface Tier {
  id: string;
  name: string;
  daily_generation_limit: number;
  concurrent_job_limit: number;
  max_resolution: string;
  storage_limit_mb: number;
  has_api_access: boolean;
  has_batch_access: boolean;
  has_priority_queue: boolean;
}

export function AdminTiers() {
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingTier, setEditingTier] = useState<Tier | null>(null);
  const [showForm, setShowForm] = useState(false);

  // Form fields
  const [formName, setFormName] = useState('');
  const [formDailyLimit, setFormDailyLimit] = useState(10);
  const [formConcurrentLimit, setFormConcurrentLimit] = useState(2);
  const [formMaxRes, setFormMaxRes] = useState('1024x1024');
  const [formStorageLimit, setFormStorageLimit] = useState(500);
  const [formApiAccess, setFormApiAccess] = useState(false);
  const [formBatchAccess, setFormBatchAccess] = useState(false);
  const [formPriorityQueue, setFormPriorityQueue] = useState(false);

  const fetchTiers = () => {
    setLoading(true);
    api
      .get<{ tiers: Tier[] }>('/admin/tiers')
      .then((res) => setTiers(res.data?.tiers ?? []))
      .catch(() => setError('Failed to load tiers'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTiers();
  }, []);

  const resetForm = () => {
    setFormName('');
    setFormDailyLimit(10);
    setFormConcurrentLimit(2);
    setFormMaxRes('1024x1024');
    setFormStorageLimit(500);
    setFormApiAccess(false);
    setFormBatchAccess(false);
    setFormPriorityQueue(false);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    const body = {
      name: formName,
      daily_generation_limit: formDailyLimit,
      concurrent_job_limit: formConcurrentLimit,
      max_resolution: formMaxRes,
      storage_limit_mb: formStorageLimit,
      has_api_access: formApiAccess,
      has_batch_access: formBatchAccess,
      has_priority_queue: formPriorityQueue,
    };
    try {
      if (editingTier) {
        await api.patch(`/admin/tiers/${editingTier.id}`, body);
      } else {
        await api.post('/admin/tiers', body);
      }
      setShowForm(false);
      setEditingTier(null);
      resetForm();
      fetchTiers();
    } catch (err) {
      if (err instanceof ApiRequestError) setError(err.error.message);
    }
  };

  const startEdit = (t: Tier) => {
    setEditingTier(t);
    setFormName(t.name);
    setFormDailyLimit(t.daily_generation_limit);
    setFormConcurrentLimit(t.concurrent_job_limit);
    setFormMaxRes(t.max_resolution);
    setFormStorageLimit(t.storage_limit_mb);
    setFormApiAccess(t.has_api_access);
    setFormBatchAccess(t.has_batch_access);
    setFormPriorityQueue(t.has_priority_queue);
    setShowForm(true);
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading tiers...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Tier Management</h1>
        <button
          onClick={() => {
            setEditingTier(null);
            resetForm();
            setShowForm(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 min-h-[44px]"
        >
          New Tier
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {editingTier ? 'Edit Tier' : 'New Tier'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Resolution</label>
                <select
                  value={formMaxRes}
                  onChange={(e) => setFormMaxRes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                >
                  <option value="1024x1024">1024×1024</option>
                  <option value="1536x1024">1536×1024</option>
                  <option value="1024x1536">1024×1536</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Daily Generation Limit</label>
                <input
                  type="number"
                  value={formDailyLimit}
                  onChange={(e) => setFormDailyLimit(Number(e.target.value))}
                  min={1}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Concurrent Job Limit</label>
                <input
                  type="number"
                  value={formConcurrentLimit}
                  onChange={(e) => setFormConcurrentLimit(Number(e.target.value))}
                  min={1}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Storage Limit (MB)</label>
                <input
                  type="number"
                  value={formStorageLimit}
                  onChange={(e) => setFormStorageLimit(Number(e.target.value))}
                  min={100}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer min-h-[44px]">
                <input
                  type="checkbox"
                  checked={formApiAccess}
                  onChange={(e) => setFormApiAccess(e.target.checked)}
                  className="rounded"
                />
                API Access
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer min-h-[44px]">
                <input
                  type="checkbox"
                  checked={formBatchAccess}
                  onChange={(e) => setFormBatchAccess(e.target.checked)}
                  className="rounded"
                />
                Batch Access
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer min-h-[44px]">
                <input
                  type="checkbox"
                  checked={formPriorityQueue}
                  onChange={(e) => setFormPriorityQueue(e.target.checked)}
                  className="rounded"
                />
                Priority Queue
              </label>
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 min-h-[44px]"
              >
                {editingTier ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingTier(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 min-h-[44px]"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tier Cards */}
      {tiers.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No tiers configured</div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tiers.map((tier) => (
          <div key={tier.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 capitalize">{tier.name}</h3>
              <button
                onClick={() => startEdit(tier)}
                className="text-xs text-blue-600 hover:text-blue-700 min-h-[44px] px-2"
              >
                Edit
              </button>
            </div>
            <dl className="space-y-1 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Daily limit</dt>
                <dd className="text-gray-900">{tier.daily_generation_limit}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Concurrent</dt>
                <dd className="text-gray-900">{tier.concurrent_job_limit}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Max resolution</dt>
                <dd className="text-gray-900">{tier.max_resolution}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Storage</dt>
                <dd className="text-gray-900">{tier.storage_limit_mb} MB</dd>
              </div>
              <div className="flex gap-2 pt-2">
                {tier.has_api_access && (
                  <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">API</span>
                )}
                {tier.has_batch_access && (
                  <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 text-xs rounded">Batch</span>
                )}
                {tier.has_priority_queue && (
                  <span className="px-1.5 py-0.5 bg-orange-50 text-orange-700 text-xs rounded">Priority</span>
                )}
              </div>
            </dl>
          </div>
        ))}
      </div>
      )}
    </div>
  );
}
